import { PRICING, WEB_DISCOUNT_PERCENT, applyWebDiscount, type PaidTier } from '@/lib/auth/feature-flags'

/**
 * The same Premium subscription carries two prices.
 *
 * - **App Store price** — `PRICING`, held at parity with the RevenueCat
 *   products (`wgit_monthly_799` / `wgit_annual_4799`).
 * - **Web price** — lower by `WEB_DISCOUNT_PERCENT` for the intervals that
 *   carry a discount, because the Stripe checkout routes attach the
 *   `STRIPE_WEB_DISCOUNT_COUPON_ID` coupon on those intervals only.
 *
 * The discount is per interval, so `active`, `label` and `percent` live on each
 * plan rather than on the group. A surface that renders an anchor price, a
 * savings badge or a "web only" line must read them from the interval it is
 * quoting, or the monthly state advertises a discount the card never gets.
 *
 * A surface must quote whichever price its own CTA actually charges. A button
 * that opens Stripe checkout quotes `web`; a button that opens the App Store
 * quotes `appStore`. `/app` is the live example of the second case — its
 * pricing card sits directly above an `AppStoreCta`, so it quotes the App Store
 * price even though it is a web page.
 */

const toDollars = (cents: number) => cents / 100

function plan(listCents: number, tier: PaidTier, interval: 'month' | 'year') {
  const percent = WEB_DISCOUNT_PERCENT[tier]
  const webCents = applyWebDiscount(listCents, tier)
  return {
    percent,
    /** False when this interval carries no discount, so surfaces drop the anchor copy. */
    active: percent > 0,
    label: `${percent}%`,
    appStoreCents: listCents,
    webCents,
    /** Dollars, for existing `${...}` interpolation on the pricing surfaces. */
    appStore: toDollars(listCents),
    web: toDollars(webCents),
    appStoreLabel: `$${toDollars(listCents).toFixed(2)}`,
    webLabel: `$${toDollars(webCents).toFixed(2)}`,
    /**
     * Same shape as `PRICING.*.display`. Renewal copy on the Stripe surfaces
     * must use these rather than `PRICING.*.display`, or one page states two
     * different renewal prices.
     */
    appStoreDisplay: `$${toDollars(listCents).toFixed(2)}/${interval}`,
    webDisplay: `$${toDollars(webCents).toFixed(2)}/${interval}`,
  }
}

export const WEB_DISCOUNT = {
  monthly: plan(PRICING.MONTHLY.amount, 'monthly', 'month'),
  yearly: plan(PRICING.YEARLY.amount, 'yearly', 'year'),
} as const

/** The web price for a Premium interval, used by any surface whose CTA is Stripe. */
export function webPlan(tier: PaidTier) {
  return WEB_DISCOUNT[tier]
}

/**
 * One sentence explaining why the web price is lower. Buyers read an unexplained
 * discount as a price that will rise later; naming the App Store fee makes it
 * read as a standing difference instead of a promotion.
 */
export const WEB_DISCOUNT_REASON = 'Buying direct skips the App Store fee, so we pass it back to you.'

/**
 * One sentence answering the objection a two-price, two-platform product
 * creates: buyers assume a web subscription buys them the web only. Shared so
 * `/pricing`, the homepage preview and `/app` state it identically.
 */
export const PREMIUM_TRANSFERS_TO_APP = 'Premium bought here also unlocks the iPhone app on the same account.'
